import { useEffect } from "react"
import { captureElement } from "@/features/preview/services/visual-diff-service"
import { reviewPreviewWithAdvertisedGemma } from "@/features/chat/services/review-preview-with-advertised-gemma"
import { reportReviewEnvelope } from "@/features/chat/services/report-review-envelope"
import { describeReviewEnvelope } from "@/features/chat/services/review-envelope-shape"
import { resolveAdvertisedAgentRuntime } from "@/features/chat/services/studio-agent-runtime-a2a"
import { usePreviewReviewClaimStore } from "./preview-review-claim-store"
import {
  outcomeFromResult,
  usePreviewReviewOutcomeStore,
} from "./preview-review-outcome-store"
import { useStudioStore } from "@/store/studio-store"

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

export function PreviewGemmaReviewBridge() {
  const claimReview = usePreviewReviewClaimStore((s) => s.claimReview)
  const setOutcome = usePreviewReviewOutcomeStore((s) => s.setOutcome)
  const workingView = useStudioStore((state) => state.workingPageView)
  const slug = useStudioStore((state) => state.projectMeta?.slug ?? null)
  const appName = useStudioStore((state) => state.projectMeta?.name ?? slug ?? "")
  const pageId = useStudioStore((state) => state.currentPageId)
  const accessToken = useStudioStore((state) => state.session?.access_token ?? null)

  useEffect(() => {
    if (typeof window === "undefined") return
    if (workingView !== "app" || !slug || !accessToken) return
    const reviewKey = `${slug}:${pageId ?? "none"}`
    if (document.querySelector('[data-testid="generated-app-rendered"]') === null) {
      return
    }
    // Claimed in a store rather than a ref: the bridge unmounts on the report
    // view and on leaving the preview rail, and a ref forgets each time.
    if (!claimReview(reviewKey)) return

    void resolveAdvertisedAgentRuntime(window.location.origin, slug).then((resolved) => {
      // No runtime to review with is a fact about the deployment, and the
      // creator was never told it — this returned silently.
      // `resolved` is non-null even when the card fetch failed — the resolver
      // returns its last attempt. The card, not the object, says whether there
      // is a runtime to review with.
      if (!resolved || resolved.card === null) {
        setOutcome({ kind: "unavailable", reviewKey })
        return
      }
      return reviewPreviewWithAdvertisedGemma({
      origin: window.location.origin,
      applicationId: resolved.applicationId,
      accessToken,
      // Already fetched by the resolver above; re-fetching it here cost every
      // reviewed page a second GET of the same agent card.
      card: resolved.card,
      scenarioId: `preview:${reviewKey}`,
      appName: appName || slug,
      userRequest: "preview visual review",
      pageCount: 1,
      messageId: crypto.randomUUID(),
      capture: captureElement,
      queryRoot: document,
      waitForPaint,
    }).then((result) => {
      // The answer used to be dropped here. Read from the review's own return
      // value so an auth failure cannot look like a success.
      setOutcome(outcomeFromResult(result, reviewKey))
      // A give-up has no envelope to describe; an answer does, whatever it
      // turned out to contain. Reporting the empty case matters most — that is
      // the one that otherwise looks like nothing arrived.
      // `"giveUp" in result` throws on null, and an unhandled rejection sets
      // the run's exit code without failing a test — six of them were coming
      // out of the impact suite while it printed "2443 passed". The predicate
      // that handles it was already exported next to the type and simply not
      // used here.
      if (result !== null && typeof result === "object" && !("giveUp" in result)) {
        reportReviewEnvelope(describeReviewEnvelope(result.body))
      }
    })
    })
  }, [accessToken, appName, claimReview, pageId, setOutcome, slug, workingView])

  return null
}
